import { useEffect, useRef } from 'react';

/**
 * Custom hook for scroll animations
 * @param {string} animationType - Type of animation (fade-up, fade-left, fade-right, fade-in, scale-in)
 * @param {number} threshold - Percentage of element visible before triggering (0 to 1)
 * @param {number} delay - Delay in milliseconds before animation starts
 * @returns {object} - Ref to attach to the element
 */
export const useScrollAnimation = (animationType = 'fade-up', threshold = 0.1, delay = 0) => {
  const elementRef = useRef(null);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    // Immediately set initial state to prevent flash
    element.style.opacity = '0';
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Add animation classes when element is visible
            setTimeout(() => {
              element.classList.add('animate-on-scroll', `animate-${animationType}`);
              
              // Remove transform and animation class after animation completes to fix blur/foil effect
              const handleAnimationEnd = () => {
                // Force remove all transforms
                element.style.transform = 'none';
                element.style.webkitTransform = 'none';
                // Remove animation classes to prevent any residual effects
                element.classList.remove('animate-on-scroll', `animate-${animationType}`);
                // Ensure opacity stays at 1
                element.style.opacity = '1';
              };
              
              element.addEventListener('animationend', handleAnimationEnd, { once: true });
            }, delay);
            
            // Unobserve after animation is triggered (optional - remove if you want repeating animations)
            observer.unobserve(element);
          }
        });
      },
      {
        threshold: threshold,
        rootMargin: '0px 0px -50px 0px' // Trigger slightly before element is fully visible
      }
    );

    observer.observe(element);

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [animationType, threshold, delay]);

  return elementRef;
};

/**
 * Hook for staggered animations on multiple children
 * @param {number} itemCount - Number of items to animate
 * @param {string} animationType - Type of animation
 * @param {number} staggerDelay - Delay between each item in milliseconds
 * @returns {array} - Array of refs for each item
 */
export const useStaggeredAnimation = (itemCount, animationType = 'fade-up', staggerDelay = 100) => {
  const refsArray = useRef([]);

  useEffect(() => {
    const observers = [];
    const currentRefs = refsArray.current;

    currentRefs.forEach((element, index) => {
      if (!element) return;

      // Immediately set initial state to prevent flash
      element.style.opacity = '0';

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setTimeout(() => {
                element.classList.add('animate-on-scroll', `animate-${animationType}`);
                
                // Remove transform and animation class after animation completes to fix blur/foil effect
                const handleAnimationEnd = () => {
                  // Force remove all transforms
                  element.style.transform = 'none';
                  element.style.webkitTransform = 'none';
                  // Remove animation classes to prevent any residual effects
                  element.classList.remove('animate-on-scroll', `animate-${animationType}`);
                  // Ensure opacity stays at 1
                  element.style.opacity = '1';
                };
                
                element.addEventListener('animationend', handleAnimationEnd, { once: true });
              }, index * staggerDelay);
              
              observer.unobserve(element);
            }
          });
        },
        {
          threshold: 0.1,
          rootMargin: '0px 0px -50px 0px'
        }
      );

      observer.observe(element);
      observers.push(observer);
    });

    return () => {
      observers.forEach((observer, index) => {
        if (currentRefs[index]) {
          observer.unobserve(currentRefs[index]);
        }
      });
    };
  }, [itemCount, animationType, staggerDelay]);

  return refsArray;
};
